'use client';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Crown, Plus, X } from 'lucide-react';
import { interpolate, useTranslation } from '../../lib/i18n';
import { ElowenApiError, isUnconfirmedWrite } from '../../lib/elowenClient';
import { normalizeText } from '../../lib/normalizeText';
import { authGeneration } from '../../lib/token';
import { rereadQuery } from '../../lib/rereadQuery';
import { useInvitable, useParticipants } from '../../lib/queries';
import { useInviteParticipant, useRemoveParticipant } from '../../lib/mutations';
import { Avatar } from '../../components/ui/Avatar';
import { Button } from '../../components/ui/Button';
import { resolveMenuLook } from '../../components/ui/menuActions';
import { RegisterSearch } from '../../components/ui/RegisterSearch';
import { Item, ItemActions, ItemContent, ItemDescription, ItemGroup, ItemMedia, ItemTitle } from '../../components/ui/shadcn/item';
import { ScrollArea } from '../../components/ui/shadcn/scroll-area';
import { ErrorState, LoadingLine, Spinner } from '../../components/ui/states';
import { useOperationFailure, useToast } from '../../components/ui/Toast';
import { Tooltip, TooltipAnchor, TooltipContent } from '../../components/ui/shadcn/tooltip';
import { useTooltipAnchor } from '../../components/ui/shadcn/useTooltipAnchor';
import type { Profile } from '../../lib/types';
import type { LocaleDict } from '../../lib/i18n/types';
import { profileAvatarUser, profileName } from './conversationListModel';

/** The shared mapping of invite refusals, also used by the composer's mention invite. */
export function inviteRefusal(error: unknown, s: LocaleDict['chat']): { message: string; close: boolean } {
  if (error instanceof ElowenApiError) {
    if (error.status === 409 && error.code === 'already_participant') return { message: s.alreadyParticipant, close: false };
    if (error.status === 409 && error.code === 'not_shareable') return { message: s.inviteFailed.notShareable, close: false };
    if (error.status === 403) return { message: s.mentionOwnerOnly, close: true };
    if (error.status === 404) return { message: s.conversationUnavailable, close: true };
  }
  return { message: s.inviteFailed.generic, close: false };
}

export function PersonAction({ label, owner, pending, disabled, onClick }: { label: string; owner?: boolean; pending?: boolean; disabled?: boolean; onClick?: () => void }) {
  const tip = useTooltipAnchor();
  return <Tooltip open={tip.open} onOpenChange={tip.onOpenChange}>
    <TooltipAnchor asChild>
      {owner ? <span role="img" aria-label={label} tabIndex={0} {...tip.hover} {...tip.focus} className="inline-flex size-8 items-center justify-center text-muted-foreground"><Crown size={14} aria-hidden /></span>
        : <Button type="button" variant="ghost-danger" size="icon-sm" aria-label={label} disabled={disabled} {...tip.hover} {...tip.focus} onClick={onClick}>
          {pending ? <Spinner tone="text-current" /> : <X size={14} aria-hidden />}
        </Button>}
    </TooltipAnchor>
    <TooltipContent side="top" className="w-auto px-2 py-1">{label}</TooltipContent>
  </Tooltip>;
}

export function PersonRow({ profile, action }: { profile: Profile; action?: ReactNode }) {
  return <Item size="xs" className="rounded-md border-0 hover:bg-accent/40 focus-within:bg-accent/40">
    <ItemMedia className="self-center"><Avatar user={profileAvatarUser(profile)} size={28} /></ItemMedia>
    <ItemContent className="gap-0"><ItemTitle>{profileName(profile)}</ItemTitle><ItemDescription>@{profile.username}</ItemDescription></ItemContent>
    {action ? <ItemActions>{action}</ItemActions> : null}
  </Item>;
}

/** Contents of the row's anchored member picker. Queries exist only while it is open. */
export function ConversationMembersPopover({ sessionId, canInvite, onClose, onLeave }: { sessionId: string; canInvite: boolean; onClose: () => void; onLeave?: () => void }) {
  const { t } = useTranslation();
  const s = t.chat;
  const leave = resolveMenuLook({ action: 'leave' }, t);
  const { toast } = useToast();
  const reportFailure = useOperationFailure();
  const qc = useQueryClient();
  // This local lock lives in the auth-scoped query cache, not in the transient popover.
  const holdKey = useMemo(() => ['conversation-member-write-hold', sessionId] as const, [sessionId]);
  const hold = useQuery<{ kind: 'pending'; userId: number } | { kind: 'uncertain'; error: unknown } | null>({ queryKey: holdKey, queryFn: async () => { throw new Error('The member write hold cannot be fetched'); }, initialData: null, enabled: false, gcTime: Infinity });
  const members = useParticipants(sessionId);
  const candidates = useInvitable(sessionId, canInvite);
  const invite = useInviteParticipant();
  const remove = useRemoveParticipant();
  const pending = hold.data?.kind === 'pending' ? hold.data.userId : null;
  const [filter, setFilter] = useState('');
  const searchRef = useRef<HTMLDivElement>(null);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    if (hold.data?.kind !== 'uncertain') return;
    const generation = authGeneration();
    reportFailure(`conversation-members:${sessionId}`, hold.data.error, {
      message: s.participantChangeFailed,
      reload: async () => {
        await qc.invalidateQueries({ queryKey: ['brain-sessions'] }, { throwOnError: true });
        if (generation !== authGeneration()) throw new Error('Authentication scope changed');
        await Promise.all([rereadQuery(members.refetch), rereadQuery(candidates.refetch)]);
        if (generation !== authGeneration()) throw new Error('Authentication scope changed');
        qc.setQueryData(holdKey, null);
      },
    });
  }, [hold.data, holdKey, sessionId, reportFailure, qc, members.refetch, candidates.refetch, s.participantChangeFailed]);
  const write = async (profile: Profile, adding: boolean) => {
    if (qc.getQueryData(holdKey)) return;
    const generation = authGeneration();
    const pendingWrite = { kind: 'pending' as const, userId: profile.id };
    qc.setQueryData(holdKey, pendingWrite);
    // The action disappears after the write; keep focus in the popover while it is disabled.
    searchRef.current?.querySelector('input')?.focus();
    try {
      if (adding) await invite.mutateAsync({ sessionId, userId: profile.id });
      else await remove.mutateAsync({ sessionId, userId: profile.id });
      if (adding && mounted.current && generation === authGeneration()) toast(interpolate(s.invited, { name: profileName(profile) }), 'ok');
    } catch (error) {
      if (generation !== authGeneration()) return;
      if (isUnconfirmedWrite(error)) qc.setQueryData(holdKey, { kind: 'uncertain', error });
      else if (mounted.current) {
        if (adding) {
          const refusal = inviteRefusal(error, s);
          toast(refusal.message, 'error', { error });
          if (refusal.close) onClose();
        } else toast(s.participantChangeFailed, 'error', { error });
      }
    } finally {
      if (generation === authGeneration() && qc.getQueryData(holdKey) === pendingWrite) qc.setQueryData(holdKey, null);
    }
  };
  const people = members.data ? [members.data.owner, ...members.data.participants] : [];
  const needle = normalizeText(filter.trim());
  const available = candidates.data?.candidates.filter(profile => !needle || normalizeText(profileName(profile)).includes(needle) || normalizeText(profile.username).includes(needle));
  return <div data-testid="conversation-members-popover" className="flex max-h-[min(70dvh,--spacing(128))] min-w-0 flex-col gap-3 overflow-y-auto">
    <h3 className="text-card-title">{s.membersCount.replace('{count}', String(people.length))}</h3>
    {members.error && !members.data ? <ErrorState message={s.participantsLoadFailed} onRetry={() => { void members.refetch(); }} />
      : !members.data ? <LoadingLine /> : <ItemGroup className="gap-0">
        {people.map((profile, index) => <PersonRow key={profile.id} profile={profile}
          action={index === 0 ? <PersonAction owner label={s.ownerBadge} /> : canInvite ? <PersonAction label={s.removeMemberLabel.replace('{name}', profileName(profile))} pending={pending === profile.id} disabled={pending !== null || hold.data !== null} onClick={() => { void write(profile, false); }} /> : undefined} />)}
      </ItemGroup>}
    {canInvite ? <div className="space-y-2 border-t border-border pt-3">
      <div ref={searchRef}><RegisterSearch value={filter} onChange={setFilter} placeholder={s.inviteHeading} onClear={() => setFilter('')} clearLabel={s.clearFilter} /></div>
      {candidates.error && !available ? <ErrorState message={s.invitableLoadFailed} onRetry={() => { void candidates.refetch(); }} />
        : !available ? <LoadingLine /> : available.length ? <ScrollArea className="[&>[data-slot=scroll-area-viewport]]:max-h-40">
          <ItemGroup className="gap-0">{available.map(profile => <PersonRow key={profile.id} profile={profile}
            action={<Button variant="ghost" size="icon-sm" type="button" disabled={pending !== null || hold.data !== null} aria-label={`${s.inviteConfirm}: ${profileName(profile)}`} onClick={() => { void write(profile, true); }}>
              {pending === profile.id ? <Spinner tone="text-current" /> : <Plus size={14} aria-hidden />}
            </Button>} />)}</ItemGroup>
        </ScrollArea> : <p className="px-3 py-1.5 text-meta text-muted-foreground">{needle ? s.noPeopleMatch : s.noInvitable}</p>}
    </div> : onLeave ? <div className="border-t border-border pt-3"><Button type="button" variant="ghost-danger" size="sm" icon={leave.icon} className="text-destructive" onClick={onLeave}>{leave.label}</Button></div> : null}
  </div>;
}
